import Button from "@atoms/button";
import { CarouselItem } from "@atoms/carousel-item";
import { Section } from "@atoms/section";
import { Typography } from "@atoms/typography";
import { CardList, ReviewerCardData } from "@molecules/homeowners/card-list/card-list";
import { HomeownersForm } from "@molecules/homeowners/form/homeowners-form";
import { Introduction, IntroductionData } from "@molecules/homeowners/introduction/introduction";
import { TrustReasons, TrustReasonsData } from "@molecules/homeowners/trust-reasons/trust-reasons";
import { useFHConfig } from "@templates/config";
import { LayoutTemplate } from "@templates/layout";
import { useTranslation } from "next-i18next";
import React, { FC, useRef } from "react";
import styled from "styled-components";

interface HomeownersData {
  coverImage: string;
  firstSection: IntroductionData;
  firstReviewCardsTitle: string;
  reviewCardsTopSection: ReviewerCardData[];
  secondReviewCardsTitle: string;
  reviewCardsBottomSection: ReviewerCardData[];
  trustReasonsTitle: string;
  trustReasons: TrustReasonsData[];
}

export interface HomeownersProps {
  homeOwnersData: HomeownersData;
}

export const HomeownersTemplate: FC<HomeownersProps> = ({ homeOwnersData }) => {
  const config = useFHConfig();
  const formRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation("homeowners");
  const scrollToButtonLabel = t("estimateButtonLabel");
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <LayoutTemplate page="homeowners">
      <CarouselItem imageStandard={`${config.backUrl}/assets/${homeOwnersData?.coverImage}`} />
      <FloatingButtonWrapper>
        <Button variant="primary" onClick={scrollToForm}>
          {scrollToButtonLabel}
        </Button>
      </FloatingButtonWrapper>
      <Section>
        <Introduction introductionData={homeOwnersData?.firstSection} scrollToForm={scrollToForm} scrollToButtonLabel={scrollToButtonLabel} />
      </Section>
      <Section>
        <Typography variant="title2">{homeOwnersData?.firstReviewCardsTitle}</Typography>
        <CardList cards={homeOwnersData?.reviewCardsTopSection} />
      </Section>
      <Section>
        <TrustReasons reasons={homeOwnersData?.trustReasons} title={homeOwnersData?.trustReasonsTitle} />
      </Section>
      <Section ref={formRef}>
        <HomeownersForm />
      </Section>
      <Section>
        <Typography variant="title2">{homeOwnersData?.secondReviewCardsTitle}</Typography>
        <CardList cards={homeOwnersData?.reviewCardsBottomSection} />
      </Section>
    </LayoutTemplate>
  );
};

const FloatingButtonWrapper = styled.div`
  position: absolute;
  top: 150px;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 2;

  @media (min-width: 768px) {
    top: 200px;
  }
`;
